/**
 * API 模块 - 认证相关
 */

import request from '../utils/request';

export const authApi = {
  /** 登录 */
  login: (data) => request.post('/auth/login', data),

  /** 注册（附带卡密） */
  register: (data) => request.post('/auth/register', data),

  /** 退出登录 */
  logout: () => request.post('/auth/logout'),

  /** 获取当前用户信息 */
  getProfile: () => request.get('/auth/profile'),

  /** 修改密码（需要旧密码验证，修改后强制重新登录） */
  changePassword: (data) => request.put('/auth/change-password', data),
};

export const cardApi = {
  /** 激活卡密 */
  activate: (card_key) => request.post('/card/activate', { card_key }),

  /** 获取卡密状态 */
  getStatus: () => request.get('/card/status'),
};

export const ruleApi = {
  /** 获取规则列表 */
  getList: (params) => request.get('/rules', { params }),

  /** 获取规则详情 */
  getDetail: (id) => request.get(`/rules/${id}`),

  /** 创建规则 */
  create: (data) => request.post('/rules', data),

  /** 更新规则 */
  update: (id, data) => request.put(`/rules/${id}`, data),

  /** 删除规则 */
  delete: (id) => request.delete(`/rules/${id}`),

  /**
   * 批量删除规则
   * @param {string[]} ids  要删除的规则 ID 数组
   */
  batchDelete: (ids) => request.delete('/rules/batch', { data: { ids } }),

  /**
   * 导入规则 ZIP 压缩包
   * @param {File} file  rule.zip 文件对象
   */
  importZip: (file) => {
    const form = new FormData();
    form.append('file', file);
    return request.post('/rules/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * 导出规则为 ZIP 压缩包
   * @param {string[]} [ids]  要导出的规则 ID 数组，不传则导出全部
   * 返回 Blob，由调用方触发浏览器下载
   */
  exportZip: (ids) => {
    const params = ids && ids.length ? { ids: ids.join(',') } : {};
    return request.get('/rules/export', { responseType: 'blob', params });
  },
};

export const sendApi = {
  /** 发送飞书消息 */
  sendFeishu: (data) => request.post('/send/feishu', data),

  /** 发送钉钉消息 */
  sendDingtalk: (data) => request.post('/send/dingtalk', data),

  /** 发送企业微信消息 */
  sendWecom: (data) => request.post('/send/wecom', data),

  /** 发送 MX 消息 */
  sendMX: (data) => request.post('/send/mx', data),

  /** 发送团员端消息 */
  sendTuanyuan: (data) => request.post('/send/tuanyuan', data),

  /** 批量发送 */
  sendBatch: (data) => request.post('/send/batch', data),
};

export const imageApi = {
  /** 上传图片到图床 */
  upload: (formData) => request.post('/image/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  /** 本地文件添加水印并上传 */
  watermark: (formData) => request.post('/image/watermark', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  /**
   * 对已上传的图床外链添加水印（无需重新上传原图）
   * @param {{ image_url, watermark_text, position, font_size, opacity, color }} data
   */
  watermarkUrl: (data) => request.post('/image/watermark-url', data),
};

/**
 * 飞书自建应用配置 API
 */
export const feishuAppApi = {
  /** 获取当前配置 */
  getConfig: () => request.get('/feishu-app/config'),

  /** 保存/更新配置（自动验证） */
  saveConfig: (data) => request.post('/feishu-app/config', data),

  /** 验证配置 */
  verify: () => request.post('/feishu-app/verify'),

  /** 上传图片到飞书图片库（获取 image_key） */
  uploadImage: (formData) => request.post('/feishu-app/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  /** 删除配置 */
  deleteConfig: () => request.delete('/feishu-app/config'),
};

export const configApi = {
  /** 导出配置 */
  export: () => request.get('/config/export', { responseType: 'text' }),

  /** 导入配置 */
  import: (config_data) => request.post('/config/import', { config_data }),


};

export const logApi = {
  /** 获取行为日志 */
  getList: (params) => request.get('/admin/logs', { params }),

  /** 获取登录记录 */
  getLoginRecords: () => request.get('/logs/login-records'),

  /** 清除所有用户操作日志 */
  clearLogs: () => request.delete('/admin/logs'),
};

export const adminApi = {
  // 统计
  getStats: () => request.get('/admin/stats'),

  // 用户管理
  getUsers: (params) => request.get('/admin/users', { params }),
  updateUserStatus: (id, status) => request.put(`/admin/users/${id}/status`, { status }),
  deleteUser: (id) => request.delete(`/admin/users/${id}`),
  resetPassword: (id, new_password) => request.put(`/admin/users/${id}/reset-password`, { new_password }),
  forceLogout: (id) => request.post(`/admin/users/${id}/force-logout`),

  // 卡密管理
  getCards: (params) => request.get('/admin/cards', { params }),
  generateCards: (data) => request.post('/admin/cards/generate', data),
  deleteCard: (id) => request.delete(`/admin/cards/${id}`),

  // 配置管理
  getConfigs: (params) => request.get('/admin/configs', { params }),

  // 日志
  getLogs: (params) => request.get('/admin/logs', { params }),
  clearLogs: () => request.delete('/admin/logs'),
  getAdminLogs: (params) => request.get('/admin/admin-logs', { params }),
  clearAdminLogs: () => request.delete('/admin/admin-logs'),

  // 系统设置
  getSystemSettings: () => request.get('/admin/system-settings'),
  saveSystemSettings: (data) => request.put('/admin/system-settings', data),
};

/**
 * 公开系统设置接口（已登录用户可访问，返回文件大小限制）
 */
export const settingsApi = {
  getPublic: () => request.get('/settings/public'),
};

/**
 * 会员水印设置接口
 */
export const watermarkApi = {
  /** 获取当前用户水印设置 */
  getSettings: () => request.get('/watermark/settings'),
  /** 保存当前用户水印设置 */
  saveSettings: (data) => request.put('/watermark/settings', data),
};
